import { z } from 'zod';
import { ANALYST_STATUSES } from '../../ontology/objects/analyst.ts';
import { type BookTerms, bookTermsOf, termsIn } from '../../ontology/objects/member.ts';
import { NO_OVERRIDES, type TermOverrides, type Terms } from '../../ontology/objects/terms.ts';
import { idSchema } from '../../ontology/primitives.ts';
import { instant } from './primitives.ts';
import { bookOrderSchema } from './recommendation.ts';

const termOverridesWire = z.object({
  riskPerTrade: z.number().nullable(),
  autopilot: z.boolean().nullable(),
}) satisfies z.ZodType<TermOverrides>;

const bookTermsWire = termOverridesWire.extend({
  bookId: idSchema,
  muted: z.boolean(),
});

const VIEWER_ROLES = ['admin', 'member'] as const;

export const viewerSchema = z.object({
  role: z.enum(VIEWER_ROLES),
  email: z.string(),
  name: z.string(),
  picture: z.url().nullable(),
  memberId: idSchema.nullable(),
  terms: termOverridesWire,
  /**
   * Read via `bookTerms` or `bookOverrides`; an absent book is heard with nothing overridden.
   * Sorted by book so the socket's frame comparison is byte-stable.
   */
  books: z.array(bookTermsWire),
  follows: z.array(idSchema),
  followedSince: z.array(z.object({ analystId: idSchema, at: instant })),
  /** When off, the desk cannot exit its own placed positions; only the fallback stop stands. */
  placingEnabled: z.boolean(),
  /** Outranks every autopilot in `terms` and `books`, which stay as said for the resume. */
  autopilotPaused: z.boolean(),
  analyst: z.object({ id: idSchema, status: z.enum(ANALYST_STATUSES) }).nullable(),
});

export type Viewer = z.infer<typeof viewerSchema>;

export function bookOverrides(viewer: Viewer, bookId: number): BookTerms | null {
  if (isDesk(viewer)) return null;
  return bookTermsOf(viewer.books.find((terms) => terms.bookId === bookId));
}

export function bookTerms(viewer: Viewer, bookId: number): ({ muted: boolean } & Terms) | null {
  if (isDesk(viewer)) return null;
  return termsIn(viewer.terms, bookOverrides(viewer, bookId) ?? undefined);
}

export const DESK_TERMS: TermOverrides = NO_OVERRIDES;

export const isDesk = (viewer: Viewer): boolean => viewer.role === 'admin';

export const sessionSchema = z.object({
  viewer: viewerSchema,
  books: bookOrderSchema,
  /** Sent here, not bundled: the desk is never tracked, and only this answer identifies the desk. */
  posthog: z.object({ projectKey: z.string(), apiHost: z.url() }).nullable().default(null),
  brokerAvailable: z.boolean().default(false),
  /** An admin is viewing the console as this member, and may change nothing. */
  viewing: z.boolean().default(false),
});

export type Session = z.infer<typeof sessionSchema>;

/**
 * Signed in but denied — not signed out: never bounce a pending member to sign-in, and let
 * `unknown` request access.
 */
const DENIALS = ['unknown', 'pending', 'rejected'] as const;

export type Denial = (typeof DENIALS)[number];

export const denialSchema = z.object({
  denial: z.enum(DENIALS),
  name: z.string(),
  email: z.string(),
  picture: z.string().nullable().default(null),
  invitedBy: z.string().nullable().default(null),
  viewing: z.boolean().default(false),
});

export type DenialBody = z.infer<typeof denialSchema>;

export const AUTH_PATHS = {
  start: '/auth/google',
  callback: '/auth/google/callback',
  logout: '/auth/logout',
  /** A link the browser navigates to, not an endpoint it fetches. */
  broker: '/auth/broker',
  /** Exists only where `DEV_SIGN_IN_EMAIL` is set; refused in production. */
  dev: '/auth/dev',
} as const;

/** What deleting the signed-in account would meet, asked before the person types anything. */
export const erasureSchema = z.object({
  email: z.string(),
  /** Each stops the deletion until it ends, in the ontology's words. */
  blockers: z.array(z.string()),
  /** Until when this sign-in may delete; null means sign in again first. */
  freshUntil: instant.nullable(),
});

export type Erasure = z.infer<typeof erasureSchema>;

/** The address typed back: which account is meant, said twice. */
export const erasureRequestSchema = z.object({ confirm: z.string().max(320) });
